import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import React, { useState, useEffect } from 'react'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { createItem, getActiveCategories } from '../../services/InventoryService'

const ItemRegisterModal = ({ open, onClose, onRegisterSuccess }) => {
    const [errors, setErrors] = useState({})
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [newEmployee, setNewEmployee] = useState({})

    const [categories, setCategories] = useState([])

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const result = await getActiveCategories()
                setCategories(result || [])
            } catch (error) {
                console.error('Error fetching positions:', error)
            }
        }

        loadCategories()
    }, [])

    const validateFields = () => {
        const newErrors = {}
        const requiredFields = ['category', 'brand', 'model', 'serialNumber', 'purchaseDate']

        requiredFields.forEach((field) => {
            if (!newEmployee[field]) {
                newErrors[field] = 'This field is required'
            }
        })

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleRegisterInputChange = (event) => {
        const { name, value } = event.target
        setNewEmployee((prev) => ({
            ...prev,
            [name]: name === 'quantity' ? Number(value) : value
        }))
    }

    const handleFormSubmit = async (event) => {
        event.preventDefault()
        setLoading(true)

        if (validateFields()) {
            try {
                console.log('newEmployee', newEmployee)
                const result = await createItem(newEmployee)

                if (result.error) {
                    setResponseMessage(result.error)
                } else {
                    setResponseMessage(result.message)
                    onRegisterSuccess()
                }
                setShowResponseDialog(true)
            } catch (error) {
                setResponseMessage('An error occurred while recording attendance.')
                setShowResponseDialog(true)
            } finally {
                setLoading(false)
                onClose()
            }
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Register Item</DialogTitle>
                <DialogContent>
                    <form onSubmit={handleFormSubmit}>
                        <Stack spacing={3} marginTop={2}>
                            {/* Form fields */}
                            <FormControl
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                error={Boolean(errors.civilStatus)}
                            >
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category"
                                    value={newEmployee.category}
                                    onChange={handleRegisterInputChange}
                                    label="Category"
                                >
                                    {categories.length > 0 ? (
                                        categories.map((categories) => (
                                            <MenuItem key={categories.id} value={categories.name}>
                                                {categories.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No employee available</MenuItem>
                                    )}
                                </Select>
                                {errors.position && <FormHelperText>{errors.position}</FormHelperText>}
                            </FormControl>
                            <TextField
                                margin="normal"
                                name="brand"
                                label="Brand"
                                fullWidth
                                variant="outlined"
                                value={newEmployee.brand}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.brand)}
                                helperText={errors.brand}
                            />

                            <TextField
                                margin="normal"
                                name="model"
                                label="Model"
                                fullWidth
                                variant="outlined"
                                value={newEmployee.model}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.model)}
                                helperText={errors.model}
                            />

                            <TextField
                                margin="normal"
                                name="serialNumber"
                                label="Serial Number"
                                fullWidth
                                variant="outlined"
                                value={newEmployee.serialNumber || ''}
                                onChange={handleRegisterInputChange}
                                error={Boolean(errors.serialNumber)}
                                helperText={errors.serialNumber}
                            />

                            {newEmployee.trackingType === 'QUANTITY' && (
                                <TextField
                                    margin="normal"
                                    name="quantity"
                                    label="Quantity"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={newEmployee.quantity || 1}
                                    onChange={handleRegisterInputChange}
                                    error={Boolean(errors.quantity)}
                                    helperText={errors.quantity}
                                />
                            )}

                            {/* Add other form fields and DatePicker fields as needed */}

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Purchase Date"
                                    value={newEmployee.purchaseDate}
                                    onChange={(purchaseDate) =>
                                        handleRegisterInputChange({
                                            target: { name: 'purchaseDate', value: purchaseDate }
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="normal"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.purchaseDate)}
                                            helperText={errors.purchaseDate}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Stack>

                        <DialogActions style={{ marginTop: '16px' }}>
                            <Button onClick={onClose} color="secondary">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                Register
                            </Button>
                        </DialogActions>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Response Dialog */}
            <Dialog open={showResponseDialog} onClose={() => setShowResponseDialog(false)}>
                <DialogTitle>Response</DialogTitle>
                <DialogContent>
                    <p>{responseMessage}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResponseDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ItemRegisterModal
